// =============================================================================
// useClinioForms — React hooks for Clinio Forms integration
// =============================================================================
// Linear: CLINIO-215 (FORMS-4)
//
// Hooks:
//   useFormsTenantStatus(clinicId)         — is the clinic provisioned?
//   useFormsTemplates(clinicId)            — list templates from Forms API
//   useSendQuestionnaire()                 — mutation: send an assignment
//   usePatientFormsAssignments(patientId)  — list local assignment mirrors
//   useResendQuestionnaire()               — mutation: resend reminder email
//   useCancelQuestionnaire()               — mutation: cancel an assignment
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { formsEdgeErrorMessage, formsEdgeHeaders, formsEdgeUrl } from '@/lib/formsEdge';
import { formsStaffAccessKey } from '@/hooks/useFormsStaffAccess';

// ── Types ────────────────────────────────────────────────────────────────────

export type FormsTenantStatus = {
  clinic_id: string;
  status: 'not_provisioned' | 'pending' | 'active' | 'disabled';
  forms_tenant_id?: string;
  default_locale?: 'da-DK' | 'en-DK';
  last_synced_at?: string | null;
  created_at?: string;
};

export type FormsTemplateSummary = {
  id: string;
  name: string;
  display_name_da?: string;
  display_name_en?: string;
  platform_template_key?: string | null;
  seed_status?: 'stub' | 'content_pending_license' | 'ready' | null;
  is_platform: boolean;
};

export type FormsAssignmentStatus =
  | 'sent'
  | 'reminded'
  | 'completed'
  | 'expired'
  | 'cancelled';

export type PatientFormsAssignmentAppointment = {
  id: string;
  starts_at: string | null;
  service_id: string | null;
  service_name: string | null;
  services: { name: string | null } | null;
};

export type PatientFormsAssignment = {
  id: string;
  clinic_id: string;
  patient_id: string;
  appointment_id: string | null;
  forms_assignment_id: string;
  forms_template_id: string;
  template_summary: {
    name: string;
    locale?: string;
    version?: string;
    scoring_method?: string;
    total_categories?: number;
    last_updated_at?: string;
  };
  status: FormsAssignmentStatus;
  sent_at: string | null;
  completed_at: string | null;
  expired_at: string | null;
  due_at: string | null;
  reminder_count: number;
  score: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  appointments?: PatientFormsAssignmentAppointment | PatientFormsAssignmentAppointment[] | null;
};

export type SendQuestionnaireInput = {
  patient_id: string;
  template_id: string;
  due_at?: string | null;
  locale?: 'da-DK' | 'en-DK';
  metadata?: Record<string, unknown>;
};

export type SendQuestionnaireOutput = {
  assignment_id: string;
  public_url: string;
  already_in_progress?: boolean;
  already_sent?: boolean;
};

// ── Query keys ───────────────────────────────────────────────────────────────

export const formsTenantStatusKey = (clinicId: string | null | undefined) =>
  ['clinicForms', 'tenantStatus', clinicId ?? null] as const;

export const formsTemplatesKey = (clinicId: string | null | undefined) =>
  ['clinicForms', 'templates', clinicId ?? null] as const;

export const patientFormsAssignmentsKey = (
  patientId: string | null | undefined,
  clinicId?: string | null,
) =>
  clinicId
    ? (['clinicForms', 'assignments', patientId ?? null, clinicId] as const)
    : (['clinicForms', 'assignments', patientId ?? null] as const);

// ── useFormsTenantStatus ─────────────────────────────────────────────────────

/**
 * Fetch the current Forms tenant provisioning status for a clinic.
 * Uses the get_clinic_forms_status RPC (non-secret fields only).
 */
export function useFormsTenantStatus(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: formsTenantStatusKey(clinicId),
    enabled: Boolean(clinicId),
    staleTime: 60_000,
    queryFn: async (): Promise<FormsTenantStatus> => {
      if (!clinicId) return { clinic_id: '', status: 'not_provisioned' };

      const { data, error } = await supabase.rpc('get_clinic_forms_status', {
        p_clinic: clinicId,
      });

      if (error) throw error;

      const d = data as Record<string, unknown>;
      return {
        clinic_id:      (d.clinic_id as string) ?? clinicId,
        status:         (d.status as FormsTenantStatus['status']) ?? 'not_provisioned',
        forms_tenant_id: d.forms_tenant_id as string | undefined,
        default_locale:  d.default_locale as 'da-DK' | 'en-DK' | undefined,
        last_synced_at:  d.last_synced_at as string | null | undefined,
        created_at:      d.created_at as string | undefined,
      };
    },
  });
}

// ── useFormsTemplates ────────────────────────────────────────────────────────

/**
 * Fetch the list of questionnaire templates available to a clinic.
 * Calls the forms-templates Edge Function which proxies to Forms
 * external-templates-list. Falls back to an empty list if the clinic
 * has no active Forms tenant.
 */
export function useFormsTemplates(clinicId: string | null | undefined) {
  const { data: tenantStatus } = useFormsTenantStatus(clinicId);
  const isActive = tenantStatus?.status === 'active';

  return useQuery({
    queryKey: formsTemplatesKey(clinicId),
    enabled: Boolean(clinicId) && isActive,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<FormsTemplateSummary[]> => {
      if (!clinicId || !isActive) return [];

      const { data: session } = await supabase.auth.getSession();
      if (!session.session) throw new Error('Not authenticated');

      const resp = await fetch(
        formsEdgeUrl('forms-list-templates', { clinic_id: clinicId }),
        {
          method: 'GET',
          headers: formsEdgeHeaders(session.session.access_token),
        },
      );

      if (!resp.ok) {
        if (resp.status === 404 || resp.status === 403) return [];
        throw new Error(`Forms templates fetch failed: ${resp.status}`);
      }

      const json = await resp.json() as { ok: boolean; data?: FormsTemplateSummary[] };
      return json.data ?? [];
    },
  });
}

// ── usePatientFormsAssignments ───────────────────────────────────────────────

/**
 * Fetch the local patient_forms_assignments mirror rows for a patient.
 * These are updated by the forms-webhook receiver (FORMS-7) when the
 * assignment completes / expires.
 */
export function usePatientFormsAssignments(
  patientId: string | null | undefined,
  clinicId?: string | null,
) {
  return useQuery({
    queryKey: patientFormsAssignmentsKey(patientId, clinicId),
    enabled: Boolean(patientId),
    staleTime: 30_000,
    queryFn: async (): Promise<PatientFormsAssignment[]> => {
      if (!patientId) return [];

      let assignmentsQuery = supabase
        .from('patient_forms_assignments')
        .select(
          'id, clinic_id, patient_id, appointment_id, forms_assignment_id, forms_template_id, ' +
          'template_summary, status, sent_at, completed_at, expired_at, due_at, ' +
          'reminder_count, score, metadata, created_at, updated_at, ' +
          'appointments ( id, starts_at, service_id, service_name, services ( name ) )',
        )
        .eq('patient_id', patientId);
      if (clinicId) assignmentsQuery = assignmentsQuery.eq('clinic_id', clinicId);
      const { data, error } = await assignmentsQuery.order('created_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as unknown as PatientFormsAssignment[];
    },
  });
}

// ── useProvisionFormsTenant ──────────────────────────────────────────────────

/**
 * Mutation: provision (or re-confirm) the Clinio Forms tenant for a clinic.
 * Calls the forms-provision-tenant Edge Function. On success the tenant
 * status query is invalidated so the UI refreshes automatically.
 */
export function useProvisionFormsTenant(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<{ forms_tenant_id: string; already_provisioned: boolean }> => {
      if (!clinicId) throw new Error('No clinic selected');

      const { data: session } = await supabase.auth.getSession();
      if (!session.session) throw new Error('Not authenticated');

      const resp = await fetch(formsEdgeUrl('forms-provision-tenant'), {
        method: 'POST',
        headers: formsEdgeHeaders(session.session.access_token, { jsonBody: true }),
        body: JSON.stringify({ clinic_id: clinicId }),
      });

      const json = await resp.json() as { ok: boolean; data?: { forms_tenant_id: string; already_provisioned: boolean }; error?: { message: string } };
      if (!resp.ok || !json.ok) {
        throw new Error(formsEdgeErrorMessage(resp.status, json, 'Could not connect the clinic to Clinio Forms'));
      }
      return json.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: formsTenantStatusKey(clinicId) });
      queryClient.invalidateQueries({ queryKey: formsTemplatesKey(clinicId) });
      queryClient.invalidateQueries({ queryKey: formsStaffAccessKey(clinicId) });
    },
  });
}

// ── useSendQuestionnaire ─────────────────────────────────────────────────────

/**
 * Mutation: send a new questionnaire assignment to a patient.
 * On success, invalidates the patient's assignment list.
 */
export function useSendQuestionnaire() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SendQuestionnaireInput): Promise<SendQuestionnaireOutput> => {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) throw new Error('Not authenticated');

      const resp = await fetch(formsEdgeUrl('forms-send-assignment'), {
        method: 'POST',
        headers: formsEdgeHeaders(session.session.access_token, { jsonBody: true }),
        body: JSON.stringify(input),
      });

      const json = await resp.json() as { ok: boolean; data?: SendQuestionnaireOutput; error?: { message: string } };
      if (!resp.ok || !json.ok) {
        throw new Error(formsEdgeErrorMessage(resp.status, json, 'Could not send the questionnaire'));
      }
      return json.data!;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: patientFormsAssignmentsKey(variables.patient_id) });
      queryClient.invalidateQueries({ queryKey: ['email-history'] });
      queryClient.invalidateQueries({ queryKey: ['treatment-courses', 'staff-view'] });
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['treatment-courses', 'staff-view'] });
    },
  });
}

// ── useResendQuestionnaire ───────────────────────────────────────────────────

/**
 * Mutation: resend the invitation email for an existing assignment.
 * Updates status to 'reminded'.
 */
export function useResendQuestionnaire() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ assignment_id }: { assignment_id: string; patient_id: string }) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) throw new Error('Not authenticated');

      const resp = await fetch(formsEdgeUrl('forms-resend-assignment'), {
        method: 'POST',
        headers: formsEdgeHeaders(session.session.access_token, { jsonBody: true }),
        body: JSON.stringify({ assignment_id }),
      });

      const json = await resp.json() as { ok: boolean; error?: { message: string } };
      if (!resp.ok || !json.ok) {
        throw new Error(formsEdgeErrorMessage(resp.status, json, 'Could not resend the questionnaire'));
      }
      return json;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: patientFormsAssignmentsKey(variables.patient_id) });
      queryClient.invalidateQueries({ queryKey: ['email-history'] });
    },
  });
}

// ── useCancelQuestionnaire ───────────────────────────────────────────────────

/**
 * Mutation: cancel an open assignment.
 * Calls Forms to cancel, then updates local status to 'cancelled'.
 */
export function useCancelQuestionnaire() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ assignment_id }: { assignment_id: string; patient_id: string }) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) throw new Error('Not authenticated');

      const resp = await fetch(formsEdgeUrl('forms-cancel-assignment'), {
        method: 'POST',
        headers: formsEdgeHeaders(session.session.access_token, { jsonBody: true }),
        body: JSON.stringify({ assignment_id }),
      });

      const json = await resp.json() as { ok: boolean; error?: { message: string } };
      if (!resp.ok || !json.ok) {
        throw new Error(formsEdgeErrorMessage(resp.status, json, 'Could not cancel the questionnaire'));
      }
      return json;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: patientFormsAssignmentsKey(variables.patient_id) });
    },
  });
}
